/* FD brand ---------------------------------------------------------------
   Furniture Done homepage: hero carousel behaviour.
   Ported from mocks/homepage.html (the hero carousel and hero-fit IIFEs).

   Two independent jobs, deliberately kept apart:
   1. the carousel itself — index sync across four stacked lists;
   2. hero fit — measuring the real chrome around the media so the control band
      always lands inside the first viewport.

   Progressive enhancement: with JS off, the first slide and its copy stay
   visible and the controls stay hidden (see the .js rules in fd-hero.css).
   Nothing in here writes copy, a URL or a colour — all of that is Liquid.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  var toArray = function (list) {
    return Array.prototype.slice.call(list);
  };

  /* Hero slides 2..n ship inside <template> so their images never race the
     slide-1 LCP download (see the comment in fd-hero-carousel.liquid).
     Promotion swaps each template for the <img> it wraps. Idempotent: after
     the first pass there are no templates left to find. */
  function promoteDeferredMedia(root) {
    toArray(root.querySelectorAll('template[data-fd-hero-defer]')).forEach(function (t) {
      var img = t.content.querySelector('img');
      if (img) t.parentNode.replaceChild(img, t);
      else t.parentNode.removeChild(t);
    });
  }

  /* ---- 1. carousel ----
     Two layouts share this one script.

     * STACK (desktop, no-JS, and the mfv-OFF phone layout) — four slides
       crossfaded in a grid stack, with the copy in three SEPARATE stacked
       lists (.fd-hero-heads / .fd-hero-texts / .fd-hero-actions) synced to the
       slide by index. Touch there is the Wave 4 gesture: read on pointerup,
       handed to go().
     * TRACK (the full-viewport phone layout, Wave 4c) — .fd-hero-slides
       becomes a REAL horizontal scroller with CSS scroll-snap, so the drag,
       the momentum and the snap belong to the browser. Wave 4b moved the
       slides with JS while the finger was down and that dies on a real thumb:
       a thumb drag is an arc, Safari sees the vertical component first, claims
       the gesture for page scroll under touch-action: pan-y and fires
       pointercancel. Native scrolling has no such failure mode — an arc is
       just a scroll — and vertical page scrolling over a full-screen hero
       still works, which touch-action: none would have cost.

     Three things make the track work, and all three are here rather than in
     the sheet because they are DOM changes, not paint:

     1. THE COPY MOVES. A scroller can only carry what is inside it, so on
        entering the track each slide's head, body and CTA panel are MOVED out
        of the three lists into that slide's own .fd-hero-slide__copy well, and
        moved back on the way out. Moved, never copied: there is exactly one of
        every node — and exactly one <h1> — in the document at all times, and
        the lists keep their child order so the stack layout is restored byte
        for byte.
     2. THE LOOP NEEDS CLONES. Liquid renders a copy of the last slide's media
        before the run and a copy of the first after it (see the comment there);
        their copy wells are filled from the real nodes here, with the <h1>
        downgraded to a div, ids stripped and every control made untabbable.
        Settling on a clone jumps the scroller instantly to the matching real
        slide and then runs show() — so the wrap is seamless and the index,
        the pips and the aria still come from the one choke point.
     3. THE INDEX FOLLOWS THE SCROLLER. scrollend (with a 120ms scroll-debounce
        fallback for engines without it) rounds scrollLeft to a slide, and
        show() scrolls the strip when the move came from a dot, an arrow or the
        auto-advance instead. `inert` on the non-current real slides keeps Tab
        from dragging the strip sideways; the clones are inert in the markup.

     The track is entered only when the script itself can guarantee both halves
     (.js + --mfv + max-width 749 + the merchant's swipe switch), which is why
     .fd-hero-c--track is written here and never inferred by a media query. */
  function initCarousel(root) {
    if (root.dataset.fdHeroReady === 'true') return;

    var slidesBox = root.querySelector('.fd-hero-slides');
    var slides = toArray(root.querySelectorAll('.fd-hero-slide'));
    var dots = toArray(root.querySelectorAll('.fd-hero-dot'));
    /* Read from the LIST containers, not by class across the whole frame: the
       clones are filled with copies that carry the same classes, and after the
       move the panels are no longer children of their list. Captured once, in
       index order, so both stay true for the life of the section. */
    var headsBox = root.querySelector('.fd-hero-heads');
    var textsBox = root.querySelector('.fd-hero-texts');
    var actsBox = root.querySelector('.fd-hero-actions');
    var byClass = function (box, cls) {
      return box
        ? toArray(box.children).filter(function (el) {
            return el.classList.contains(cls);
          })
        : [];
    };
    var heads = byClass(headsBox, 'fd-hero-head');
    var texts = byClass(textsBox, 'fd-hero-text');
    var acts = byClass(actsBox, 'fd-hero-acts');
    var playBtn = root.querySelector('.fd-hc-play');
    var prevBtn = root.querySelector('.fd-hc-prev');
    var nextBtn = root.querySelector('.fd-hc-next');
    if (slides.length < 2 || !playBtn || !prevBtn || !nextBtn) return;

    root.dataset.fdHeroReady = 'true';
    root.classList.add('js');

    /* Promote the hidden slides once the LCP has had the network to itself:
       window load normally, immediately if load already fired, and at once
       on any carousel interaction so a fast tap never shows an empty frame. */
    var promote = function () {
      promoteDeferredMedia(root);
    };
    if (document.readyState === 'complete') promote();
    else window.addEventListener('load', promote, { once: true });
    /* show(0) below seeds the carousel during init, long before load — and it
       runs through the same choke point every interaction does. This flag is
       what keeps that seeding call from promoting the slides straight back
       into the LCP's network window; it flips once seeding is done. */
    var seeded = false;

    var speed = parseInt(root.dataset.fdHeroSpeed, 10);
    if (!speed || speed < 1000) speed = 6000;
    var autorotate = root.dataset.fdHeroAutorotate !== 'false';

    var i = 0;
    var timer = null;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* Reduced motion (and the merchant's autorotate switch) SEED the carousel
       paused rather than hiding the control: hiding it would leave no way to
       ever start the slideshow, and the paused control states the situation
       honestly. Pressing play is an explicit request and outranks the OS
       preference — which is why start() gates on `paused`, never on `reduced`. */
    var paused = reduced || !autorotate;

    /* ---- track state ---------------------------------------------------- */
    /* fail-open: the attribute missing (older render, or a stale node) means
       enabled; only an explicit "false" from the merchant switch opts out. */
    var swipeOn = root.dataset.fdSwipe !== 'false';
    var trackMq = window.matchMedia('(max-width: 749px)');
    var clones = toArray(root.querySelectorAll('[data-fd-clone]'));
    var wells = slides.map(function (s) {
      return s.querySelector('.fd-hero-slide__copy');
    });
    var track = false;
    var scrollTimer = null;
    var resizeTimer = null;

    var frameW = function () {
      if (!slidesBox) return 0;
      return slidesBox.clientWidth || Math.round(root.getBoundingClientRect().width) || 0;
    };

    var trackable = function () {
      return !!(
        swipeOn &&
        slidesBox &&
        wells.length === slides.length &&
        wells.every(Boolean) &&
        root.classList.contains('fd-hero-c--mfv') &&
        trackMq.matches
      );
    };

    /* pos is a STRIP position, not a slide index: 0 is the last-slide clone,
       1..n the real slides, n+1 the first-slide clone. */
    function scrollToPos(pos, smooth) {
      if (!slidesBox) return;
      var left = pos * frameW();
      if (smooth && !reduced && slidesBox.scrollTo) {
        slidesBox.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        slidesBox.scrollLeft = left;
      }
    }

    /* Every real slide but the current one is inert: without it Tab moves into
       an off-screen CTA and the browser scrolls the strip to reach it. */
    function applyInert() {
      slides.forEach(function (s, k) {
        if (k === i) s.removeAttribute('inert');
        else s.setAttribute('inert', '');
      });
    }

    function fillClone(clone, n) {
      if (!clone) return;
      var well = clone.querySelector('.fd-hero-slide__copy');
      if (!well) return;
      well.innerHTML = '';
      [heads[n], texts[n], acts[n]].forEach(function (src) {
        if (!src) return;
        var copy = src.cloneNode(true);
        copy.classList.remove('is-on');
        copy.removeAttribute('aria-hidden');
        copy.removeAttribute('id');
        /* A clone is a PICTURE of a slide, never a second one: no duplicate
           <h1> (the class carries every style, so a div renders identically),
           no duplicate ids, and no focusable control even on an engine that
           does not implement `inert`. */
        toArray(copy.querySelectorAll('h1')).forEach(function (h) {
          var div = document.createElement('div');
          div.className = h.className;
          div.innerHTML = h.innerHTML;
          h.parentNode.replaceChild(div, h);
        });
        toArray(copy.querySelectorAll('[id]')).forEach(function (el) {
          el.removeAttribute('id');
        });
        toArray(copy.querySelectorAll('a, button, input, select, textarea')).forEach(
          function (el) {
            el.setAttribute('tabindex', '-1');
          }
        );
        well.appendChild(copy);
      });
    }

    var cloneFor = function (kind) {
      for (var k = 0; k < clones.length; k++) {
        if (clones[k].getAttribute('data-fd-clone') === kind) return clones[k];
      }
      return null;
    };

    /* touch-action is written by the script rather than the sheet so it exists
       only where the pointerup gesture is actually bound. In the track it must
       NOT be set at all: pan-y on an ancestor would stop the scroller panning
       horizontally, which is the whole feature. */
    function setTouchAction() {
      if (!swipeOn) return;
      root.style.touchAction = 'pan-y';
      root.style.touchAction = 'pan-y pinch-zoom';
    }

    function enterTrack() {
      if (track) return;
      track = true;
      root.style.touchAction = '';
      slides.forEach(function (s, k) {
        if (heads[k]) wells[k].appendChild(heads[k]);
        if (texts[k]) wells[k].appendChild(texts[k]);
        if (acts[k]) wells[k].appendChild(acts[k]);
      });
      fillClone(cloneFor('last'), slides.length - 1);
      fillClone(cloneFor('first'), 0);
      /* class first, pin second, in the SAME task: the strip only becomes a
         scroller once the class is on, and nothing can paint between the two
         statements — so the last-slide clone at scrollLeft 0 is never seen. */
      root.classList.add('fd-hero-c--track');
      scrollToPos(i + 1, false);
      applyInert();
    }

    function exitTrack() {
      if (!track) return;
      track = false;
      root.classList.remove('fd-hero-c--track');
      /* back into the lists in index order, so the child-order the stack
         layout and every :first-child rule rely on is exactly as rendered */
      heads.forEach(function (p) {
        if (headsBox) headsBox.appendChild(p);
      });
      texts.forEach(function (p) {
        if (textsBox) textsBox.appendChild(p);
      });
      acts.forEach(function (p) {
        if (actsBox) actsBox.appendChild(p);
      });
      clones.forEach(function (c) {
        var well = c.querySelector('.fd-hero-slide__copy');
        if (well) well.innerHTML = '';
      });
      slides.forEach(function (s) {
        s.removeAttribute('inert');
      });
      if (slidesBox) slidesBox.scrollLeft = 0;
      setTouchAction();
      show(i);
    }

    function show(n, fromScroll) {
      /* every user path — dots, arrows, editor select, autorotation, and the
         scroller settling — lands here, so this is the single place
         interaction has to promote from. */
      if (seeded) promote();
      var raw = n;
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) {
        s.classList.toggle('is-on', k === i);
        s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
      });
      /* aria-current, not aria-selected: the dots are plain buttons, not a tab
         widget (see the comment beside the markup in fd-hero-carousel.liquid). */
      dots.forEach(function (d, k) {
        d.classList.toggle('is-on', k === i);
        if (k === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      /* Three lists, one index. In the stack they are children of their list;
         in the track they have been moved into the slides — the index is the
         captured array position either way, so nothing here has to know which
         layout is live. */
      [heads, texts, acts].forEach(function (list) {
        list.forEach(function (p, k) {
          var on = k === i;
          p.classList.toggle('is-on', on);
          p.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
      });
      if (!track) return;
      applyInert();
      if (!seeded || fromScroll) return;
      /* A move that did NOT come from the scroller has to drive it. raw, not
         i: asking for one past the last slide (auto-advance, next arrow) means
         scroll FORWARD onto the first-slide clone and let the settle jump take
         it home — scrolling back to position 1 across the whole strip is the
         one thing that would look like a rewind. Same the other way at -1. */
      var pos = raw === slides.length ? slides.length + 1 : raw === -1 ? 0 : i + 1;
      scrollToPos(pos, !reduced);
    }

    /* The scroller has stopped: round to a slide, and if it stopped on a clone
       jump instantly onto the real one it copies before telling show() about
       it. The jump is a plain scrollLeft write — there is no scroll-behavior
       on the element, so it cannot animate and the seam is never seen. */
    function onSettle() {
      if (!track || !slidesBox) return;
      var w = frameW();
      if (!w) return;
      var last = slides.length - 1;
      var pos = Math.round(slidesBox.scrollLeft / w);
      var real = pos - 1;
      if (pos <= 0) real = last;
      else if (pos > last + 1) real = 0;
      if (pos <= 0 || pos > last + 1) slidesBox.scrollLeft = (real + 1) * w;
      show(real, true);
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (paused) return;
      stop();
      timer = setInterval(function () {
        show(i + 1);
      }, speed);
    }

    /* Arrows and dots keep working while paused: start() no-ops, so a manual
       move never silently resumes the auto-advance the visitor switched off.
       The same no-op is what stops hover-pause resuming it on mouseleave. */
    function go(n) {
      show(n);
      start();
    }

    function syncPlay() {
      playBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      playBtn.setAttribute('aria-label', paused ? playBtn.dataset.labelPlay : playBtn.dataset.labelPause);
    }

    playBtn.addEventListener('click', function () {
      paused = !paused;
      syncPlay();
      if (paused) {
        stop();
      } else {
        start();
      }
    });
    syncPlay();

    prevBtn.addEventListener('click', function () {
      go(i - 1);
    });
    nextBtn.addEventListener('click', function () {
      go(i + 1);
    });
    dots.forEach(function (d, k) {
      d.addEventListener('click', function () {
        go(k);
      });
    });

    /* ---- the scroller ---- */
    if (slidesBox) {
      slidesBox.addEventListener(
        'scroll',
        function () {
          /* first touch of the strip, whatever started it: the neighbour about
             to slide in must not be an empty frame. Same `seeded` guard show()
             uses, so the init pass still cannot pull the deferred images into
             the LCP's network window. */
          if (seeded) promote();
          if (!track) return;
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(onSettle, 120);
        },
        { passive: true }
      );
      /* scrollend where it exists (Chromium, and Safari 26+); the debounce
         above is the fallback everywhere else. Both are idempotent. */
      slidesBox.addEventListener(
        'scrollend',
        function () {
          if (!track) return;
          clearTimeout(scrollTimer);
          onSettle();
        },
        { passive: true }
      );
    }

    var syncMode = function () {
      if (trackable()) enterTrack();
      else exitTrack();
    };
    /* A resize changes the slide width, so the strip has to be re-pinned to
       the current slide or it settles onto the neighbour. Debounced: an iOS
       URL-bar collapse fires a burst of them. */
    var onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        syncMode();
        if (track) scrollToPos(i + 1, false);
      }, 60);
    };
    if (trackMq.addEventListener) trackMq.addEventListener('change', syncMode);
    else if (trackMq.addListener) trackMq.addListener(syncMode);
    window.addEventListener('resize', onResize);

    /* ---- touch ----
       Touch and pen only. Mouse pointers are ignored on purpose: a desktop
       drag across a photo is a text/image drag, not a carousel gesture, so
       nothing above 749px changes — a tablet touch still works, which is
       wanted.

       In TRACK mode there is no gesture code at all: the browser owns the
       drag. The one thing kept is the autorotate rule — the FIRST touch on the
       hero stops the auto-advance for good and the play control reports that
       honestly (user ruling 2026-09-12). A visitor who has started touching
       the hero is driving it.

       In STACK mode (mfv off, or 750px and up) the Wave 4 gesture stands
       unchanged: read on pointerup, |dx| >= 40 and clearly more horizontal
       than vertical, handed to the same go() the chevrons use.

       Bound on the FRAME, not on .fd-hero-slides: in the full-viewport phone
       layout the copy/controls band is a SIBLING of the stage laid over it, so
       a listener on the stage alone would miss most of the screen. A drag long
       enough to commit is past every browser's tap slop, so a swipe that
       happens to start on a CTA still swipes and does not activate it.

       preventDefault is never called anywhere here, so a vertical drag scrolls
       the page untouched in both modes. */
    if (swipeOn) {
      var swipe = null; // the one gesture in flight (stack mode)
      var downCount = 0; // live non-mouse pointers, for the multi-touch refusal

      var stopRotate = function () {
        if (!paused) {
          paused = true;
          stop();
          syncPlay();
        }
        if (seeded) promote();
      };

      /* touchstart as well as pointerdown: a scroll that the compositor takes
         over immediately can land without a pointerdown reaching us. */
      root.addEventListener('touchstart', stopRotate, { passive: true });

      root.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType === 'mouse') return;
          stopRotate();
          downCount += 1;
          if (track) return;
          /* a second finger abandons the gesture rather than re-seeding it:
             pinch-zoom and two-finger scrolls must never advance the hero */
          if (downCount > 1) {
            swipe = null;
            return;
          }
          /* iOS back-swipe corridor: a gesture that STARTS there belongs to
             Safari, so it is left alone entirely rather than consumed */
          if (e.clientX <= 24) return;
          swipe = { x: e.clientX, y: e.clientY, id: e.pointerId };
        },
        { passive: true }
      );

      var swipeEnd = function (e) {
        if (e.pointerType === 'mouse') return;
        downCount = downCount > 0 ? downCount - 1 : 0;
        var g = swipe;
        swipe = null;
        if (track || !g || e.type !== 'pointerup' || e.pointerId !== g.id) return;
        var dx = e.clientX - g.x;
        var dy = e.clientY - g.y;
        /* commit thresholds: far enough to be deliberate, and clearly more
           horizontal than vertical so a diagonal scroll never steals a slide */
        if (Math.abs(dx) < 40) return;
        if (Math.abs(dx) <= Math.abs(dy) * 1.5) return;
        go(dx < 0 ? i + 1 : i - 1);
      };
      root.addEventListener('pointerup', swipeEnd, { passive: true });
      root.addEventListener('pointercancel', swipeEnd, { passive: true });

      setTouchAction();
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    /* Handles for the theme editor and for teardown. Dawn's theme-editor.js
       only knows about .slideshow__slide, so a fork with its own class names
       has to wire the block-select behaviour itself or selecting a slide in the
       editor does nothing. Deselect calls start(), which still no-ops while
       paused — so a merchant who pressed pause keeps their pause. */
    root.fdHero = {
      select: function (slide) {
        var n = slides.indexOf(slide);
        if (n === -1) return;
        show(n);
        stop();
      },
      deselect: start,
      /* The window/media-query listeners outlive the section node the theme
         editor throws away, and every one of them would write scroll state
         onto a frame nobody can see. */
      destroy: function () {
        stop();
        clearTimeout(scrollTimer);
        clearTimeout(resizeTimer);
        window.removeEventListener('resize', onResize);
        if (trackMq.removeEventListener) trackMq.removeEventListener('change', syncMode);
        else if (trackMq.removeListener) trackMq.removeListener(syncMode);
      },
    };

    /* the track has to be up before the first show(), so the copy is already
       inside the slides when the ladder is seeded */
    syncMode();
    show(0);
    seeded = true;
    if (track) scrollToPos(i + 1, false);
    start();
  }

  /* ---- 2. hero fit ----
     --fd-hero-chrome is everything in the first viewport that is NOT the hero
     media: the sticky header above it, the control band below it, and the
     hero's own bottom padding. The CSS carries tuned constants as a no-JS
     fallback, but they are per-breakpoint guesses and go stale the moment the
     band rewraps — so when JS is available we measure the real thing and write
     it onto the section element, which outranks every sheet rule.
     No feedback loop: nothing in the measurement depends on the media's height. */
  function initFit(hero) {
    if (hero.dataset.fdHeroFit === 'true') return;
    var frame = hero.querySelector('.fd-hero-c');
    var slides = hero.querySelector('.fd-hero-slides');
    var band = hero.querySelector('.fd-hero-band');
    if (!frame || !slides || !band) return;
    hero.dataset.fdHeroFit = 'true';

    var header = document.querySelector('.fd-hdr-wrap');
    var queued = null;
    var lastAbove = null;

    function measure() {
      queued = clearTimeout(queued);
      /* the slides' offset from the top of the document = the built header
         (plus anything else above the hero) */
      var above = slides.getBoundingClientRect().top + window.pageYOffset;
      /* The header compacts on scroll (96 -> 68 desktop, 64 -> 56 mobile) and,
         being sticky, it is still in flow — so a measurement taken while it is
         compact reports LESS chrome than the visitor gets back at the top of
         the page, and the band would spill under the fold on the way up. So a
         compact-state measurement reuses the last full-height figure; the first
         non-compact remeasure corrects it either way.
         Transient: on a page loaded already scrolled, the first-ever
         measurement IS the compact one and seeds lastAbove with it, so the
         chrome runs ~28px short until the header next expands. Harmless — the
         only cost is a slightly taller media for one scroll-to-top, and the
         alternative (hard-coding the expanded header height) is exactly the
         stale constant this script exists to replace. */
      if (header && header.classList.contains('is-compact') && lastAbove !== null) {
        above = lastAbove;
      } else {
        lastAbove = above;
      }
      /* The full-viewport phone layout puts the band OVER the stage, so "chrome below
         the media" no longer exists and the measured figure would shrink the stage to
         nothing. Zero that property there and hand the CSS the one number it cannot
         work out for itself: the REAL height of everything above the stage. The sheet
         only knows --header-height, which is the header alone — this store also runs
         an announcement bar above it, so the sheet's figure is ~38px short and the
         stage overshot the glass dock by that much. Writing the measured offset is
         what makes the stage end exactly where the dock's pill begins.
         Fail-open: JS off, or this branch never reached, and the var() falls back to
         --header-height — a slightly tall stage, never a broken one.
         measure() re-fires on resize and orientationchange, so rotating a phone or
         crossing 749px re-evaluates this branch and restores the real measurement. */
      if (
        frame.classList.contains('fd-hero-c--mfv') &&
        window.matchMedia('(max-width: 749px)').matches
      ) {
        frame.style.setProperty('--fd-hero-chrome', '0px');
        frame.style.setProperty('--fd-hero-top', Math.round(above) + 'px');
        return;
      }
      /* above 749px, or with the phone layout off, the property must not linger */
      frame.style.removeProperty('--fd-hero-top');
      var cs = getComputedStyle(hero);
      var bandCs = getComputedStyle(band);
      var chrome =
        above +
        band.getBoundingClientRect().height +
        (parseFloat(bandCs.marginTop) || 0) +
        (parseFloat(cs.paddingBottom) || 0);
      /* +8: the measurement is exact, so without a buffer the band's bottom
         lands sub-pixel from the viewport edge — one browser's rounding away
         from spilling past the fold. */
      frame.style.setProperty('--fd-hero-chrome', Math.ceil(chrome) + 8 + 'px');
    }

    /* setTimeout, not rAF: rAF is paused in a background tab, which leaves the
       measurement stale until the tab is looked at again. A short debounce
       fires either way. */
    function queue() {
      clearTimeout(queued);
      queued = setTimeout(measure, 60);
    }

    measure();
    /* metrics move as fonts settle and as the band rewraps */
    window.addEventListener('load', queue);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
    window.addEventListener('resize', queue);
    window.addEventListener('orientationchange', queue);
    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(queue);
      ro.observe(band);
      /* the root too: emulated/embedded viewports can change size without
         firing a window resize event, and the band alone may not change height
         with them */
      ro.observe(document.documentElement);
      /* and the header, whose compact state changes the chrome above us */
      if (header) ro.observe(header);
    }

    /* The window listeners and the observers on <html>/the header outlive the
       section node the theme editor throws away — and every one of them writes
       a custom property onto a frame that is no longer on the page. Released by
       the shopify:section:unload hook below. (The observers on the band are
       released with it, but disconnect() takes all three at once.) */
    hero.fdHeroFitDestroy = function () {
      clearTimeout(queued);
      window.removeEventListener('load', queue);
      window.removeEventListener('resize', queue);
      window.removeEventListener('orientationchange', queue);
      if (ro) ro.disconnect();
      /* document.fonts.ready is a settled promise by now on all but the first
         paint; queue() on a detached frame is harmless, so it is left alone. */
    };
  }

  function init(scope) {
    toArray((scope || document).querySelectorAll('.fd-hero')).forEach(function (hero) {
      initCarousel(hero.querySelector('.fd-hero-c') || hero);
      initFit(hero);
    });
  }

  /* Theme editor. Dawn's theme-editor.js handles its own sections by class
     name and ignores ours entirely, so every editor hook this section needs is
     wired here. `event.target` is the section wrapper for section events and
     the block's own element — the <article class="fd-hero-slide"> — for block
     events, because that is what carries block.shopify_attributes. */
  function frameFor(node) {
    var hero = node.closest ? node.closest('.fd-hero') : null;
    return hero ? hero.querySelector('.fd-hero-c') : null;
  }

  init(document);

  /* the editor re-renders a section into a fresh DOM node */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* ...and throws the old node away without unloading its timer or its
     listeners. The two jobs tear down separately because they attach
     separately: the carousel can bail (fewer than two slides) while hero fit
     still ran, and fit hangs off .fd-hero while the carousel hangs off
     .fd-hero-c. */
  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target.querySelector) return;
    var frame = event.target.querySelector('.fd-hero-c');
    if (frame && frame.fdHero) frame.fdHero.destroy();
    var hero = event.target.querySelector('.fd-hero');
    if (hero && hero.fdHeroFitDestroy) hero.fdHeroFitDestroy();
  });

  /* Selecting a slide in the editor previews it and holds it there; deselecting
     hands control back to start(), which still no-ops if the merchant had
     pressed pause. */
  document.addEventListener('shopify:block:select', function (event) {
    var frame = frameFor(event.target);
    if (frame && frame.fdHero) frame.fdHero.select(event.target);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var frame = frameFor(event.target);
    if (frame && frame.fdHero) frame.fdHero.deselect();
  });
})();
